import React, { useCallback, useContext, useMemo } from 'react';
import { SurveyContext } from '@bingads-webui-campaign-react/survey-context';
import { ChoiceGroup } from '@fluentui/react';
import { Option } from './option';
import { QuestionTitle } from './title';
import { SurveyContextType } from '../../../survey-context';
import { generateLabelId } from '../../../utility';

function optionComponentFactory(option, dynamicOption) {
  const {
    error,
    data,
    setData,
    mySetDirtyState,
    dirtyState,
    isSelected,
    logActivity,
  } = dynamicOption;

  return {
    onRenderField: (props, render) => (
      <Option
        option={option}
        error={error}
        data={data}
        setData={setData}
        dirtyState={dirtyState}
        setDirtyState={mySetDirtyState}
        isSelected={isSelected}
        logActivity={logActivity}
      >
        {render(props)}
      </Option>
    ),
  };
}

function buildSingleSelectionOptions(options, dynamicOption) {
  const {
    setOptionData,
    data,
    error,
    mySetDirtyState,
    dirtyState,
    logActivity,
  } = dynamicOption;

  return options.map((option) => {
    const {
      key,
      text,
    } = option;

    return {
      key,
      text,
      ariaLabel: text,
      ...optionComponentFactory(option, {
        setData: setOptionData,
        data: data[key],
        error: error[key],
        dirtyState: dirtyState[key],
        mySetDirtyState,
        isSelected: data.selectedOption === key,
        logActivity,
      }),
    };
  });
}

export const ChoiceGroupView = React.memo((props: {
  configurator: any,
  data: any,
  setData: any,
  dirtyState: any,
  setDirtyState: any,
}) => {
  const {
    configurator,
    data,
    setData,
    dirtyState,
    setDirtyState,
  } = props;

  const {
    options,
    key: questionKey,
  } = configurator.getConfiguration();

  const {
    selectedOption,
  } = data;

  const error = useMemo(() => configurator.getError(data), [configurator, data]);

  const setOptionData = useCallback((optionKey, value) => {
    const newData = {
      ...data,
      [optionKey]: value,
      selectedOption: value.length > 0 ? optionKey : selectedOption,
    };
    setData(newData);
  }, [data, selectedOption, setData]);

  const mySetDirtyState = useCallback((key, value) => {
    const newDirtyState = {
      ...dirtyState,
      [key]: value,
    };
    setDirtyState(newDirtyState);
  }, [dirtyState, setDirtyState]);

  // @ts-ignore
  const surveyContext : SurveyContextType = useContext(SurveyContext);
  const {
    logActivity,
    getSurveyAnswers,
  } = surveyContext;

  const myLogActivity = useCallback((log: string) => {
    logActivity(`Question: ${configurator.key}, type: single-selection, ${log}`);
  }, [configurator.key, logActivity]);

  const singleSelectionOptions = useMemo(() => buildSingleSelectionOptions(
    options,
    {
      setOptionData,
      data,
      error,
      mySetDirtyState,
      dirtyState,
      logActivity: myLogActivity,
    }
  ), [options, setOptionData, data, error, mySetDirtyState, dirtyState, myLogActivity]);

  const answers = getSurveyAnswers();

  const title = useMemo(() => configurator.getTitle(answers), [answers, configurator]);
  const labelKey = useMemo(() => generateLabelId(questionKey), [questionKey]);

  return (
    <ChoiceGroup
      styles={{
        root: {
          '& .customized-option-input': { marginLeft: '32px' },
          '& .customized-option .ms-ChoiceFieldLabel': { paddingLeft: '32px' },
          '& .ms-ChoiceField-wrapper': { width: '100%' },
          '& .ms-ChoiceField': { marginTop: '4px', marginBottom: '4px' },
        },
      }}
      ariaLabelledBy={labelKey}
      aria-label={title}
      className="choice-group"
      options={singleSelectionOptions}
      selectedKey={selectedOption}
      onChange={(ev, option) => {
        mySetDirtyState('isDirty', true);
        setData({
          ...data,
          selectedOption: option?.key,
        });
        myLogActivity(`action: select option, key: ${option?.key}`);
      }}
    />);
});

interface SingleSelectionViewProps {
  configurator: any,
  data: {
    selectedOption: string,
    [key: string]: string | undefined,
  },
  setData: any,
  rootClassName: string | undefined,
  dirtyState: any,
  setDirtyState: any,
}

export const SingleSelectionView = (props: SingleSelectionViewProps) => {
  const {
    configurator,
    data,
    setData,
    dirtyState,
    setDirtyState,
    rootClassName,
  } = props;

  const error = useMemo(() => configurator.isAnswered(data), [configurator, data]);
  const {
    isDirty,
  } = dirtyState;

  return (
    <div className={rootClassName || 'single-selection'} >
      <QuestionTitle isDirty={isDirty} configurator={configurator} error={error} >
        <ChoiceGroupView
          configurator={configurator}
          setDirtyState={setDirtyState}
          dirtyState={dirtyState}
          setData={setData}
          data={data}
        />
      </QuestionTitle>
    </div>
  );
};
